import React from "react";

import "regenerator-runtime/runtime";

import Vue from "vue";
import store from "@/store/store";

import BootstrapVue from "bootstrap-vue";
import VueMoment from "vue-moment";
import moment from "moment-timezone";

import "./design-system/styles/global.scss";
import "./design-system/assets/fonts/MyFontsWebfontsKit.css";

import App from "./App.vue";
import router from "@/router/router";
import { routerGuard } from "@/router/routerGuard";

import i18n from "@/i18n";
import VueAnalytics from "vue-analytics";
import VueGtm from "vue-gtm";
import config from "@/config";
import VueCurrencyFilter from "vue-currency-filter";
import Chakra, { CThemeProvider } from "@chakra-ui/vue";
import iloqTheme from "@iloqshared/design-tokens/dist/theme";
import { AuthReferences } from "@iloq/shared-vue";
import { AuthInterceptor } from "./api/AuthInterceptor";
import AuthStore from "./AuthStore";
import VersionInterceptor from "./api/VersionInterceptor";

window.React = React;

Vue.config.productionTip = false;

AuthReferences.setAuthInterceptorsReferenceFn(AuthInterceptor.setAuthInterceptors);
AuthReferences.setVersionInterceptorReferenceFn(VersionInterceptor.setVersionInterceptor);
AuthReferences.setHasRefreshTokenFn(AuthStore.hasRefreshToken);

router.beforeEach(routerGuard);
router.beforeEach((to, from, next) => {
  store.commit("globalSearch/setInSearchResultPage", to.path === "/search");
  next();
});

const breakpoints = {
  sm: "30em",
  md: "48em",
  lg: "62em",
  xl: "80em",
  "2xl": "96em",
  // custom breakpoint
  "3xl": "108em",
};

const theme = {
  ...iloqTheme,
  baseStyles: {
    CButton: {
      background: "transparent",
      paddingLeft: "3xl",
      paddingRight: "3xl",
      fontWeight: "normal",
      borderWidth: "1px",
      borderColor: "brand.purple",
      borderRadius: "full",
      borderStyle: "solid",
    },
    CFormControl: {
      marginBottom: "lg",
    },
    CFormLabel: {
      fontWeight: "normal",
      fontSize: "md",
      paddingBottom: { base: "4px", md: "0" },
      // For coloring the required asterisk
      "& > span": {
        color: "brand.greenDark",
      },
    },
    CInput: {
      fontSize: "1rem !important",
      borderRadius: "input",
    },
    CTextarea: {
      fontSize: "1rem !important",
      borderRadius: "input",
    },
    CHeading: {
      lineHeight: "1.2",
      fontWeight: "normal",
    },
  },
  breakpoints,
};

const chakraIcons = iloqTheme.icon.chakra;
type IconNames = keyof typeof chakraIcons;

const customIcons = Object.keys(chakraIcons)
  .map((iconName) => ({
    [iconName]: {
      path: chakraIcons[iconName as IconNames].path,
      viewBox: chakraIcons[iconName as IconNames].viewBox,
    },
  }))
  .reduce((prev, curr) => ({ ...prev, ...curr }), {});

Vue.use(Chakra, {
  extendTheme: theme,
  icons: {
    extend: customIcons,
  },
});
Vue.use(BootstrapVue);
Vue.use(VueMoment, {
  moment,
});
if (config.get("gaId")) {
  Vue.use(VueAnalytics, {
    router,
    id: config.get("gaId"),
    ignoreRoutes: ["tokens"],
  });
}
if (config.get("gtmId")) {
  Vue.use(VueGtm, {
    id: config.get("gtmId"),
    debug: true,
    vueRouter: router,
    ignoredViews: ["tokens"],
  });
}

Vue.use(VueCurrencyFilter, {
  symbol: "€",
  thousandsSeparator: " ",
  fractionCount: 2,
  fractionSeparator: ",",
  symbolPosition: "end",
  symbolSpacing: true,
});

Vue.prototype.$bus = new Vue();

// Vue.prototype.$t = i18n.t.bind(i18n);

new Vue({
  router,
  store,
  i18n,
  render: (h) => h(CThemeProvider, [h(App)]),
}).$mount("#app");
